import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, Users, FileText, BarChart3, Shield, Zap } from 'lucide-react';
import { useAuth } from '../App';
import ContactModal from './ContactModal';

const LandingPage = () => {
  const { isAuthenticated, isProfessional, isIndividual } = useAuth();
  const [showContactModal, setShowContactModal] = useState(false);

  // Redirect authenticated users to their portal
  if (isAuthenticated) {
    window.location.href = isProfessional ? '/professional' : '/individual';
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="container mx-auto px-4 py-6">
        <nav className="flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-2 hover:opacity-80 transition-opacity">
            <TrendingUp className="w-8 h-8 text-blue-600" />
            <span className="text-2xl font-bold text-gray-900">Finance Vitals</span>
          </Link>
          <div className="space-x-4">
            <Link 
              to="/login" 
              className="text-gray-600 hover:text-gray-900 font-medium"
            >
              Sign In
            </Link>
            <Link 
              to="/register" 
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 font-medium"
            >
              Get Started
            </Link>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            Is your business financially healthy?
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed mb-8">
            Upload your financial statements and get an instant health score, red flags, and tailored next steps — no subscription or accounting knowledge required.
          </p>
          
          {/* Free Checkup CTA */}
          <div className="mb-12">
            <Link 
              to="/free-checkup"
              className="bg-green-600 hover:bg-green-700 text-white font-bold py-4 px-8 rounded-lg text-lg transition-colors duration-200 shadow-lg hover:shadow-xl inline-block"
            >
              Free Financial Checkup
            </Link>
            <p className="text-sm text-gray-500 mt-2">No account needed</p>
          </div>
          
          {/* Portal Selection */}
          <div className="grid md:grid-cols-1 gap-8 mt-12 max-w-xl mx-auto">
            {/* Individual Portal */}
            <div className="bg-white rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-shadow duration-300">
              <FileText className="w-12 h-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-2xl font-semibold text-gray-800 mb-4">
                For Business Owners
              </h3>
              <p className="text-gray-600 mb-6">
                Get instant financial health analysis of your QuickBooks data with AI-powered insights and recommendations.
              </p>
              <ul className="text-left text-gray-600 mb-8 space-y-2">
                <li className="flex items-center">
                  <Zap className="w-4 h-4 text-green-500 mr-2" />
                  Instant AI analysis
                </li>
                <li className="flex items-center">
                  <BarChart3 className="w-4 h-4 text-green-500 mr-2" />
                  Visual financial reports
                </li>
                <li className="flex items-center">
                  <Shield className="w-4 h-4 text-green-500 mr-2" />
                  Health score & red flags
                </li>
                <li className="flex items-center">
                  <Users className="w-4 h-4 text-green-500 mr-2" />
                  Connect with your accountant
                </li>
              </ul>
              <Link 
                to="/register?role=individual" 
                className="bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 inline-block"
              >
                Start Free Analysis
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="bg-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-12">
              Why Choose Finance Vitals?
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <TrendingUp className="w-12 h-12 text-blue-500 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-800 mb-2">AI-Powered Analysis</h3>
                <p className="text-gray-600 text-sm">
                  AI-powered analysis delivers professional-grade financial insights
                </p>
              </div>
              <div className="text-center">
                <Shield className="w-12 h-12 text-green-500 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-800 mb-2">Secure & Private</h3>
                <p className="text-gray-600 text-sm">
                  Your financial data is encrypted and never shared without permission
                </p>
              </div>
              <div className="text-center">
                <Zap className="w-12 h-12 text-purple-500 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-800 mb-2">Instant Results</h3>
                <p className="text-gray-600 text-sm">
                  Get comprehensive analysis and recommendations in under 2 minutes
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-blue-600 py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Improve Your Financial Health?
          </h2>
          <p className="text-blue-100 mb-8 max-w-2xl mx-auto">
            Built with accountants and business owners in mind for professional financial diagnostics.
          </p>
          <Link 
            to="/register" 
            className="bg-white text-blue-600 font-semibold py-3 px-8 rounded-lg hover:bg-gray-100 transition-colors duration-200 inline-block"
          >
            Get Started Free
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {/* Brand */}
              <div className="md:col-span-2">
                <div className="flex items-center space-x-2 mb-4">
                  <TrendingUp className="w-8 h-8 text-blue-400" />
                  <span className="text-2xl font-bold">Finance Vitals</span>
                </div>
                <p className="text-gray-400 mb-4">
                  AI-powered financial health diagnostic platform for small businesses. 
                  Get instant insights into your financial data with professional-grade analysis.
                </p>
                <p className="text-sm text-gray-400 mb-2">
                  Support:{' '}
                  <a
                    href="mailto:support@finance-vitals.com"
                    className="text-blue-400 hover:text-white transition-colors"
                    data-testid="footer-support-email-link"
                  >
                    support@finance-vitals.com
                  </a>
                </p>
                <p className="text-sm text-gray-400 mb-2">
                  <button
                    type="button"
                    onClick={() => setShowContactModal(true)}
                    className="text-blue-400 hover:text-white transition-colors underline"
                    data-testid="footer-contact-support-button"
                  >
                    Contact support
                  </button>
                </p>
                <p className="text-sm text-gray-500">
                  © 2026 Finance Vitals. All rights reserved.
                </p>
              </div>

              {/* Quick Links */}
              <div>
                <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
                <ul className="space-y-2">
                  <li>
                    <Link to="/free-checkup" className="text-gray-400 hover:text-white transition-colors">
                      Free Checkup
                    </Link>
                  </li>
                  <li>
                    <Link to="/register?role=professional" className="text-gray-400 hover:text-white transition-colors">
                      For Professionals
                    </Link>
                  </li>
                  <li>
                    <Link to="/register?role=individual" className="text-gray-400 hover:text-white transition-colors">
                      For Business Owners
                    </Link>
                  </li>
                  <li>
                    <Link to="/login" className="text-gray-400 hover:text-white transition-colors">
                      Sign In
                    </Link>
                  </li>
                </ul>
              </div>

              {/* Legal */}
              <div>
                <h4 className="text-lg font-semibold mb-4">Legal</h4>
                <ul className="space-y-2">
                  <li>
                    <Link to="/terms" className="text-gray-400 hover:text-white transition-colors">
                      Terms of Use
                    </Link>
                  </li>
                  <li>
                    <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors">
                      Privacy Policy
                    </Link>
                  </li>
                </ul>
                
                <div className="mt-6">
                  <h5 className="text-sm font-semibold mb-2 text-gray-300">Disclaimer</h5>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    AI-generated analysis for informational purposes only. 
                    Not a substitute for professional financial advice.
                  </p>
                </div>
              </div>
            </div>

            <div className="border-t border-gray-800 mt-8 pt-8 text-center">
              <p className="text-sm text-gray-400">
                Finance Vitals uses advanced AI to provide financial insights. 
                Always consult with qualified professionals for financial decisions.
              </p>
            </div>
          </div>
        </div>
      </footer>

      <ContactModal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
        variant="general"
        source="landing_footer"
      />
    </div>
  );
};

export default LandingPage;
